import { useCallback } from 'react';

/**
 * Custom hook for synchronizing notification actions across components
 * Provides centralized notification management with event broadcasting
 */
export const useNotificationSync = () => {
    // Broadcast notification updates to all listening components
    const broadcastNotificationUpdate = useCallback((eventType, data) => {
        const event = new CustomEvent('notificationUpdate', {
            detail: { eventType, data }
        });
        window.dispatchEvent(event);
    }, []);

    // Mark notification as read and broadcast the update
    const markAsReadSync = useCallback(async (notificationId, api) => {
        try {
            await api.put(`/api/notifications/${notificationId}/read`);
            broadcastNotificationUpdate('markAsRead', { notificationId });
            return true;
        } catch (error) {
            console.error('Error marking notification as read:', error);
            return false;
        }
    }, [broadcastNotificationUpdate]);

    // Mark all notifications as read and broadcast the update
    const markAllAsReadSync = useCallback(async (api) => {
        try {
            await api.put('/api/notifications/mark-all-read');
            broadcastNotificationUpdate('markAllAsRead', {});
            return true;
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            return false;
        }
    }, [broadcastNotificationUpdate]);

    // Dismiss notification and broadcast the update
    const dismissNotificationSync = useCallback(async (notificationId, api) => {
        try {
            await api.put(`/api/notifications/${notificationId}/dismiss`);
            broadcastNotificationUpdate('dismiss', { notificationId });
            return true;
        } catch (error) {
            console.error('Error dismissing notification:', error);
            return false;
        }
    }, [broadcastNotificationUpdate]);

    // Broadcast new notification arrival
    const broadcastNewNotification = useCallback((notificationData) => {
        broadcastNotificationUpdate('newNotification', notificationData);
    }, [broadcastNotificationUpdate]);

    // Set up listener for notification updates from other components
    const setupNotificationListener = useCallback((callback) => {
        const handleNotificationUpdate = (event) => {
            callback(event.detail);
        };

        window.addEventListener('notificationUpdate', handleNotificationUpdate);
        
        // Return cleanup function
        return () => {
            window.removeEventListener('notificationUpdate', handleNotificationUpdate);
        };
    }, []);

    return {
        markAsReadSync,
        markAllAsReadSync,
        dismissNotificationSync,
        setupNotificationListener,
        broadcastNotificationUpdate,
        broadcastNewNotification
    };
};